/**
 * The confirm before saving a launch's audience or a group (22 Oct 2026, Jasmine): people who stop
 * matching lose the course from To do / In progress (kept under Completed if they finished it) and
 * leave that launch's reports. Asks the server who that would be; nobody → saves straight away.
 *
 *   const check = useAudienceCheck(orgId)
 *   check.guard({ group: { id, rule } }, () => save())
 *   …
 *   {check.dialog}
 */
import { useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { orgApi, type AudienceLeavers, type LaunchTargetInput } from '@/lib/orgApi'
import { ConfirmDialog } from '@/components/org/orgUi'
import type { ConfirmSpec } from '@/components/org/orgHelpers'

type Change = { launchId: string; targets: LaunchTargetInput[] } | { group: { id?: string; rule?: unknown; members?: string[] } }
  | { people: Array<{ userId: string; values: Record<string, unknown> }> }

const names = (ps: Array<{ name: string }>) => ps.length <= 3 ? ps.map(p => p.name).join(', ') : `${ps.slice(0, 3).map(p => p.name).join(', ')} and ${ps.length - 3} more`

export function useAudienceCheck(orgId: string | null | undefined): { guard: (change: Change, save: () => void | Promise<void>) => Promise<void>; dialog: ReactNode; checking: boolean } {
  const [spec, setSpec] = useState<ConfirmSpec | null>(null)
  const [checking, setChecking] = useState(false)
  // After every save through here, bring the launches up to date at once (23 Oct 2026) — whoever
  // left or came back — instead of on the next page load anyone makes; what's on screen should match.
  const apply = async (save: () => void | Promise<void>) => {
    await save()
    // Some callers save without waiting (fire-and-forget): give the save a moment to land first.
    if (orgId) setTimeout(() => void orgApi.myLearning(orgId).catch(() => {}), 1500)
  }
  async function guard(change: Change, save: () => void | Promise<void>) {
    if (!orgId) return void (await save())
    let courses: AudienceLeavers
    setChecking(true)
    // If the check itself fails, save as before — the rule still applies; only the warning is missed.
    try { courses = (await orgApi.audienceLeavers(orgId, change)).courses } catch { courses = [] } finally { setChecking(false) }
    // Nobody loses anything — but someone may be coming BACK into an audience (23 Oct 2026: Sana's
    // Region set back to South waited for the next page load), so bring the launches up to date too.
    if (!courses.length) return void (await apply(save))
    const people = new Set(courses.flatMap(c => c.people.map(p => p.id))).size
    const done = courses.some(c => c.people.some(p => p.completed))
    setSpec({
      title: `${people} ${people === 1 ? 'person' : 'people'} will lose ${courses.length === 1 ? 'a course' : `${courses.length} courses`}`,
      // Short (22 Oct 2026): who loses what, and one line on what that means.
      body: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {courses.map(c => <li key={c.launchId}><strong style={{ fontWeight: 600 }}>{c.title}</strong> — {names(c.people)}</li>)}
          </ul>
          <span>The course will be taken off their list, and their progress won’t count in its reports.{done ? ' Anyone who finished it keeps it under Completed.' : ''}</span>
        </div>
      ),
      cta: 'Save', run: () => apply(save),
    })
  }
  // While the server works out who is affected (a second or two): say so, so Save doesn't look dead.
  const dialog = <>
    {checking && (
      <div role="status" style={{ position: 'fixed', inset: 0, zIndex: 69, background: 'rgba(28,26,34,.18)', display: 'grid', placeItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 12, padding: '14px 18px', boxShadow: '0 12px 32px rgba(40,20,70,.18)', fontSize: 14.5, color: '#1C1A22' }}>
          <Loader2 size={18} className="animate-spin" color="#7A3BD9" /> Saving…
        </span>
      </div>
    )}
    <ConfirmDialog spec={spec} onClose={() => setSpec(null)} />
  </>
  return { guard, dialog, checking }
}
